import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { type SchmancyMarkColor, type ToneVariant, toneColors } from '@schmancy/theme/tone'
import { css, html } from 'lit'
import { property } from 'lit/decorators.js'

/**
 * The edge mark of the tone family — a tone-filled strip running down one
 * edge of a card, carrying a short identifier read bottom-to-top the way a
 * book spine carries its title. It is what a row's identity looks like when
 * the row has no horizontal budget left for it: the number stays legible and
 * costs a strip's width instead of a line of the card's body.
 *
 * The host is the strip. Drop it as the first child of a
 * `grid-cols-[auto_1fr] items-stretch` card and it takes the card's full
 * height with no height prop, because the strip's height is the row's, never
 * its own content's.
 *
 * Content is slotted, so the caller owns the type scale it needs — a
 * `<schmancy-typography>` of any token — while this element owns the
 * rotation, the tone, and the centring. `variant="soft"` is the resting
 * identity strip; `variant="pinned"` is the one that is also reporting a
 * state — a strip is a filled AREA, so its ground is the rung that does not
 * flip with the scheme, and `solid` stays the rung for a mark small enough
 * that only the saturated ink survives.
 *
 * Slot nothing and the strip is the bare rail: pure colour down an edge,
 * carrying no content padding, exactly as wide as the host says — a padding
 * sum wider than a border-box width would become the used width, so a rail
 * asked for 4px would paint 24 over its neighbour's column.
 */
@schmancyElement('schmancy-tone-spine')
export class SchmancyToneSpine extends SchmancyElement {
	static styles = [
		css`
			:host {
				display: grid;
				flex-shrink: 0;
			}

			/* Cascade-critical and utility-invisible in one element. Tailwind
			   ships no writing-mode utility at all, so the only spelling
			   available in a class is an arbitrary value whose emission
			   depends on which build happened to scan this file — and a
			   spine whose writing-mode silently went missing renders its
			   identifier horizontally and blows the card's column geometry
			   open. The 180° turn is what makes it read bottom-to-top (a
			   spine, not a sideways caption) and is declared alongside it so
			   no utility can half-apply against the pair. Nothing on this
			   element carries a rotate-* or whitespace-* class, so no utility
			   is being shadowed here. */
			[part='text'] {
				writing-mode: vertical-rl;
				text-orientation: mixed;
				transform: rotate(180deg);
				white-space: nowrap;
			}
		`,
	]

	/** Which tone the strip carries, or a categorical series index — the same
	 *  two-vocabulary `color` `<schmancy-tone-dot>` takes, since a spine is the
	 *  edge mark of one entity as often as it is the state of one. Ignored by
	 *  `variant="hollow"`, the un-toned state. */
	@property({ reflect: true }) color: SchmancyMarkColor = 'surface'

	/** How hard the strip presses its tone — `soft` is the resting identity
	 *  strip, `pinned` the one reporting a state. */
	@property({ type: String, reflect: true }) variant: ToneVariant = 'soft'

	render() {
		const carriesIdentifier = (this.textContent ?? '').trim() !== '' || this.childElementCount > 0
		const toned = toneColors(this.color, this.variant)
		return html`
			<div
				part="strip"
				class="${this.classMap({
					'w-full h-full grid place-items-center': true,
					'px-3 py-4': carriesIdentifier,
					'transition-colors duration-200': true,
				})}"
				style="${this.styleMap(toned)}"
			>
				<span part="text"><slot @slotchange=${() => this.requestUpdate()}></slot></span>
			</div>
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-tone-spine': SchmancyToneSpine
	}
}
